import { useState } from "react";
import { useAuth, formatApiError } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { KeyRound, LogOut } from "lucide-react";

export default function AccountPage() {
  const { user, logout, changePassword } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("New password and confirmation do not match.");
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="account-page">
      <PageHeader
        eyebrow="Account"
        title="Your account"
        subtitle="Signed in as the Indie Law admin."
        testId="account-header"
      />

      <div className="bg-white border border-[#141B4D]/15 rounded-sm p-6 md:p-8 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Meta label="Email" value={user?.email} />
          <Meta label="Name" value={user?.name || "—"} />
          <Meta label="Role" value={user?.role || "—"} />
        </div>
        <button
          onClick={logout}
          data-testid="account-logout-button"
          className="inline-flex items-center gap-2 bg-transparent border border-[#141B4D]/20 text-[#141B4D] px-4 py-2 rounded-sm text-sm hover:bg-[#141B4D]/5"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="bg-white border border-[#141B4D]/15 rounded-sm p-6 md:p-8 max-w-2xl">
        <div className="flex items-center gap-3 mb-5">
          <div className="bg-[#141B4D]/5 p-2 rounded-sm">
            <KeyRound size={14} className="text-[#141B4D]" strokeWidth={2.5} />
          </div>
          <h2 className="text-lg font-bold text-[#141B4D]">Change password</h2>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <PasswordField
            label="Current password"
            value={current}
            onChange={setCurrent}
            testId="current-password-input"
          />
          <PasswordField
            label="New password"
            value={next}
            onChange={setNext}
            testId="new-password-input"
            hint="At least 8 characters."
          />
          <PasswordField
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            testId="confirm-password-input"
          />
          {err && (
            <div
              data-testid="change-password-error"
              className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2"
            >
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="change-password-submit"
            className="bg-[#141B4D] text-[#F2E9DB] px-5 py-2 rounded-sm text-sm font-medium hover:bg-[#141B4D]/90 disabled:opacity-60"
          >
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.1em] text-[#141B4D]/60 mb-1">
        {label}
      </div>
      <div className="text-sm text-[#141B4D]">{value}</div>
    </div>
  );
}

function PasswordField({ label, value, onChange, hint, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
        {label}
      </label>
      <input
        data-testid={testId}
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
      />
      {hint && (
        <div className="text-xs text-[#141B4D]/55 mt-1">{hint}</div>
      )}
    </div>
  );
}
